/**
 * 商品图片 Service —— PC 商品编辑与移动端商品编辑的唯一图片写通道（2026-09-19 图片管理收口）
 *
 * 后端真值：products.images（JSON URL 数组）+ products.image ≡ images[0]（同步规则，服务端强制）。
 * 全部操作走专用 API（server/routes/image-admin.js，enqueueDbTx 事务安全），
 * 禁止前端整对象 PUT 覆盖图片字段（并发会互相清掉对方刚保存的图）。
 *
 * 语义（与管理端三处 UI 一致，UI 可不同、语义必须相同）：
 *   add(productId, url)        新增 = currentImages + [url]，追加到末尾，不抢主图
 *   remove(productId, url)     删除 = 移除引用（不物理删文件），删的是 images[0] 时服务端自动顺延主图
 *   reorder(productId, urls)   排序 = 同集合重排（服务端校验集合一致，防并发期间增删被覆盖）
 *   setPrimary(productId, url) 设主图 = 置顶到 [0]（语义等价于「调整到第 1 位」，无第二个 is_primary）
 *   applyDelta(...)            PC 保存时把「初始快照 → 编辑终态」的差异翻译成上述原子操作序列
 */
import axios from 'axios'

const BASE = '/image-admin/products'

function unwrap (res) {
  const d = res && res.data
  if (!d || d.code !== 200) {
    throw new Error((d && d.message) || '操作失败')
  }
  return d.data
}

/** 新增图片（追加末尾，不抢主图） */
export async function addProductImage (productId, url) {
  return unwrap(await axios.post(`${BASE}/${productId}/images`, { url }))
}

/** 删除图片（移除引用；删主图时服务端顺延第二张为主图）。
 *  注意：项目 axios 0.20 的 axios.delete(url, {data}) 实测不发送 body（后端 400 缺 url），
 *  必须走通用 axios({method:'DELETE', data}) 形式（Lite 端同理用显式 request 携带 body）。 */
export async function removeProductImage (productId, url) {
  return unwrap(await axios({ method: 'DELETE', url: `${BASE}/${productId}/images`, data: { url } }))
}

/** 重排序（同集合重排；顺序即数组序，第 1 位即主图） */
export async function reorderProductImages (productId, urls) {
  return unwrap(await axios.patch(`${BASE}/${productId}/images/order`, { urls }))
}

/** 设为主图（置顶 [0]，等价于调整到第 1 位） */
export async function setPrimaryProductImage (productId, url) {
  return unwrap(await axios.patch(`${BASE}/${productId}/images/primary`, { url }))
}

/**
 * 差异应用（PC 编辑表单「保存」时使用）：把 initial → final 的变化拆成原子操作。
 * 顺序：先删（服务端逐个移除）→ 再增（追加末尾）→ 最后重排（终态顺序一次落定）。
 * 并发容错：期间他人已删同图 → 删除 404 视作已达目的；重排集合不一致 → 抛错由调用方提示刷新。
 * @param {string} productId
 * @param {string[]} initial 打开表单时的图片快照
 * @param {string[]} final  保存时的图片终态
 * @param {boolean} [forceOrder] final 与服务端集合一致但顺序不同/相同都发重排（默认 true，保证终态）
 */
export async function applyImageDelta (productId, initial, final, forceOrder = true) {
  const init = (initial || []).filter(Boolean)
  const end = (final || []).filter(Boolean)
  const initSet = new Set(init)
  const endSet = new Set(end)
  const removed = init.filter(u => !endSet.has(u))
  const added = end.filter(u => !initSet.has(u))
  for (const url of removed) {
    try { await removeProductImage(productId, url) } catch (e) {
      // 他人已删同图/商品已删 → 目的已达成；其余错误（网络/权限）如实抛出
      if (!/404|不存在|不在商品图集/.test(String(e && e.message))) throw e
    }
  }
  for (const url of added) {
    await addProductImage(productId, url)
  }
  if (forceOrder && end.length) {
    // 新增是追加到末尾的，终态顺序必须显式落一次
    await reorderProductImages(productId, end)
  }
  return { removed: removed.length, added: added.length }
}
